import { Phone, Mail, MapPin, Clock, Leaf } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { CONTACT } from "@/data/site";
import { useSEO, webPageSchema } from "@/lib/seo";
import { PAGE_META } from "@/data/page_meta";


export default function Contact() {
  useSEO({ ...PAGE_META.contact, jsonLd: webPageSchema({ path: PAGE_META.contact.path, name: PAGE_META.contact.title, description: PAGE_META.contact.description, type: "ContactPage" }) });
  return (
    <div data-testid="contact-page">
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(95,165,68,0.10),transparent_45%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">Contact</p>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-navy leading-[0.92] max-w-3xl">
              Let's talk about your space.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Whether you're looking for recurring residential cleaning or a customized commercial cleaning proposal,
              we'd love to learn more about your space. Complete the form below and we'll respond within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-start">
          {/* INFO */}
          <Reveal className="lg:col-span-5">
            <div className="bg-navy text-white rounded-3xl p-8 md:p-10 grain relative overflow-hidden">
              <div className="relative z-10">
                <h2 className="font-heading text-2xl font-semibold mb-8">Get in touch</h2>
                <ul className="space-y-6">
                  <InfoRow icon={MapPin} label="Service area" value={CONTACT.area} />
                  <InfoRow icon={Phone} label="Call or text" value={CONTACT.phone} href={CONTACT.phoneHref} />
                  <InfoRow icon={Mail} label="Email" value={CONTACT.email} href={`mailto:${CONTACT.email}`} />
                  <InfoRow icon={Clock} label="Hours" value={CONTACT.hours.map((h) => `${h.d}: ${h.h}`).join("\n")} />
                </ul>
                <div className="mt-10 pt-8 border-t border-white/10 flex items-center gap-3 text-leaf">
                  <Leaf className="w-5 h-5" />
                  <span className="text-sm text-white/80">Green Cleaning included on every job — no upcharge.</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* FORM */}
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-border">
              <h2 className="font-heading text-2xl font-semibold text-navy mb-1">Send us a message</h2>
              <p className="text-muted-foreground mb-7">We typically reply within one business day.</p>
              <LeadForm type="contact" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAP */}
      <section className="pb-28" data-testid="contact-map-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-6 h-6 text-leaf" />
              <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-navy">
                Serving all around the DMV
              </h2>
            </div>
            <p className="text-muted-foreground max-w-2xl mb-8">
              From our Alexandria office we care for homes and businesses across Washington DC, Maryland, and
              Northern Virginia — including Arlington, Fairfax, Alexandria, Tysons, Vienna, McLean, Falls Church and beyond.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ServiceAreaMap />
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href }) {
  const content = (
    <div className="flex items-start gap-4" data-testid={`contact-${label}`}>
      <span className="w-11 h-11 shrink-0 rounded-2xl bg-white/10 flex items-center justify-center"><Icon className="w-5 h-5 text-leaf" /></span>
      <div>
        <p className="text-xs uppercase tracking-widest text-white/50">{label}</p>
        <p className="text-lg font-medium whitespace-pre-line">{value}</p>
      </div>
    </div>
  );
  return href ? <li><a href={href} className="hover:opacity-80 transition-opacity block">{content}</a></li> : <li>{content}</li>;
}
